/**
 * §12.4 — a console's accent, derived from one seed colour.
 *
 * Every console has always had exactly one accent hue, hard-coded per
 * `data-portal` in styles.css (§3). A brand's own colour (owner request 4,
 * 2026-09-22) breaks that: the hex comes from `brands.theme_accent` at
 * runtime, so the eight tokens it expands into (`--a`, `--a-deep`, `--a-ink`,
 * `--a-wash` for light; `--ad`, `--ad-deep`, `--ad-ink`, `--ad-wash` for dark)
 * have to be *derived*, and derived in a way that never ships text nobody can
 * read. §7 promises 4.5:1 for accent text on every surface in both themes —
 * that promise cannot depend on which colour an operator typed in a field.
 *
 * The derivation moves **lightness only** — hue and saturation are kept, so
 * the colour a brand chose is still recognisably that colour, just pushed
 * dark enough (light theme) or light enough (dark theme) to read. HSL, not
 * OKLCH: this kit carries no colour-science dependency, HSL's H and S are
 * exactly "hue" and "roughly chroma" in the terms the brief asks for, and a
 * matrix-based OKLab round trip would buy perceptual uniformity this file
 * does not need — every candidate the search below produces is checked
 * against the real WCAG contrast formula, not assumed from the model. HSL's
 * lightness is not perceptually uniform, but the guarantee comes from that
 * check, not from the model being linear.
 *
 * Pure functions throughout, except {@link useAccentOverride}: nothing here
 * touches `document` until a component asks it to.
 */
import { useLayoutEffect } from 'react';

import type { KitMessageKey } from './messages.ts';

/** `#` + six lowercase-or-uppercase hex digits. Nothing shorter, nothing with alpha. */
const HEX_RE = /^#[0-9a-f]{6}$/i;

export function isAccentHex(s: string): boolean {
  return HEX_RE.test(s);
}

export interface AccentTokens {
  readonly light: {
    readonly a: string;
    readonly aDeep: string;
    readonly aInk: string;
    readonly aWash: string;
  };
  readonly dark: {
    readonly ad: string;
    readonly adDeep: string;
    readonly adInk: string;
    readonly adWash: string;
  };
  /** True when `light.a` had to move to hold its contrast — the seed hex itself failed it. */
  readonly adjusted: boolean;
  /** The dark-theme twin of {@link adjusted}: true when `dark.ad` had to move —
   *  tracked separately because a seed can fail one theme's contrast (including
   *  its wash, §12.1) without failing the other's. `adjusted` alone keeps its
   *  existing meaning ("the light value moved") for callers already reading it. */
  readonly adjustedDark: boolean;
}

/* ------------------------------------------------------------------ *
 * Colour arithmetic — hex ⇄ RGB ⇄ HSL, and WCAG contrast
 * ------------------------------------------------------------------ */

function hexToRgb(hex: string): readonly [number, number, number] {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function clampByte(v: number): number {
  return Math.max(0, Math.min(255, Math.round(v)));
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => clampByte(v).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

interface Hsl {
  readonly h: number; // degrees, [0, 360)
  readonly s: number; // [0, 1]
  readonly l: number; // [0, 1]
}

function rgbToHsl(r: number, g: number, b: number): Hsl {
  const rn = r / 255, gn = g / 255, bn = b / 255;
  const max = Math.max(rn, gn, bn), min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0));
  else if (max === gn) h = (bn - rn) / d + 2;
  else h = (rn - gn) / d + 4;
  return { h: h * 60, s, l };
}

function hexToHsl(hex: string): Hsl {
  const [r, g, b] = hexToRgb(hex);
  return rgbToHsl(r, g, b);
}

function hueChannel(p: number, q: number, t0: number): number {
  let t = t0;
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

function hslToRgb(h: number, s: number, l: number): readonly [number, number, number] {
  if (s === 0) {
    const v = l * 255;
    return [v, v, v];
  }
  const hn = (((h % 360) + 360) % 360) / 360;
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hueChannel(p, q, hn + 1 / 3) * 255,
    hueChannel(p, q, hn) * 255,
    hueChannel(p, q, hn - 1 / 3) * 255,
  ];
}

function hslToHex(h: number, s: number, l: number): string {
  const [r, g, b] = hslToRgb(h, s, l);
  return rgbToHex(r, g, b);
}

/** WCAG 2.x relative luminance (the sRGB piece of the contrast formula). */
function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex);
  const lin = (c: number) => {
    const cs = c / 255;
    return cs <= 0.04045 ? cs / 12.92 : Math.pow((cs + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

/** WCAG contrast ratio between two colours, order-independent (§7). */
export function contrastRatio(hexA: string, hexB: string): number {
  const la = relativeLuminance(hexA);
  const lb = relativeLuminance(hexB);
  const hi = Math.max(la, lb), lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** `fraction` of `hex` over solid white — the same mix a 10% wash is (verified against the shipped `--a-wash` tokens in styles.css). */
function mixOverWhite(hex: string, fraction: number): string {
  return mixOver(hex, fraction, '#ffffff');
}

/**
 * `fraction` of `hex` over an arbitrary solid base — the general form of
 * {@link mixOverWhite}, needed because the dark wash (`--ad-wash`) is a true
 * translucent `rgb(r g b / 13%)`, so what a reader actually sees behind it
 * depends on which surface it sits on (`--raised` or `--raised-2`), unlike
 * the light wash below, which is baked to one opaque hex at derivation time.
 */
function mixOver(hex: string, fraction: number, base: string): string {
  const [r, g, b] = hexToRgb(hex);
  const [br, bg, bb] = hexToRgb(base);
  const mix = (c: number, bc: number) => c * fraction + bc * (1 - fraction);
  return rgbToHex(mix(r, br), mix(g, bg), mix(b, bb));
}

function rgbSpacePercent(hex: string, alphaPercent: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgb(${clampByte(r)} ${clampByte(g)} ${clampByte(b)} / ${alphaPercent}%)`;
}

function withLightness(hex: string, deltaL: number): string {
  const { h, s, l } = hexToHsl(hex);
  return hslToHex(h, s, Math.max(0, Math.min(1, l + deltaL)));
}

/* ------------------------------------------------------------------ *
 * The four surfaces the tokens are guaranteed against
 *
 * Copied from styles.css's `:root` (dark) and the light theme block (§4) —
 * not imported, because this runs before any stylesheet is necessarily
 * parsed (a hex typed into `AccentPicker` before the page has painted). The
 * scratchpad contrast script for this batch reads styles.css directly and
 * would catch the two drifting.
 * ------------------------------------------------------------------ */
const LIGHT_RAISED = '#ffffff';
const LIGHT_RAISED_2 = '#f9f9f7'; // --raised-2, light theme
const LIGHT_GROUND = '#f5f5f2';
const LIGHT_INK = '#16171b'; // --ink, light theme
const DARK_RAISED = '#15171b';
const DARK_GROUND = '#0e0f12';
const DARK_RAISED_2 = '#1b1d22';
const DARK_NEAR_BLACK = '#0e0f12'; // --ground, dark theme — the plain fallback for adInk

const MIN_CONTRAST = 4.5;
/** Lightness search resolution: 240 steps end-to-end, so every candidate hex changes by at most one unit in one RGB channel from the last — fine enough that the search never leaps past the exact boundary the shipped tokens sit on. */
const STEPS = 240;
/** How much darker (light theme) / lighter (dark theme) the hover shade sits — matched to the ~7-9 percentage-point gap already on the shipped per-portal tokens (e.g. operator `--a` #1f55d0 → `--a-deep` #1945ad is 7.9pp). */
const DEEP_DELTA = 0.08;
/** The wash opacity each theme derives at, kept as one constant so the check
 *  below can never drift from what `accentTokens` actually ships (§12.1
 *  finding: `darkMeets` used to check solid surfaces only, so a wash that
 *  came out short of 4.5:1 slipped through on every preset). */
const LIGHT_WASH_FRACTION = 0.1;
const DARK_WASH_ALPHA = 13;

function lightMeets(hex: string): boolean {
  if (contrastRatio(hex, LIGHT_RAISED) < MIN_CONTRAST) return false;
  if (contrastRatio(hex, LIGHT_GROUND) < MIN_CONTRAST) return false;
  // The wash is opaque (baked over white at derivation time — see
  // `mixOverWhite`), so it reads the same wherever it sits; checked over both
  // raised surfaces anyway, in case that ever stops being true.
  if (contrastRatio(hex, mixOver(hex, LIGHT_WASH_FRACTION, LIGHT_RAISED)) < MIN_CONTRAST) return false;
  if (contrastRatio(hex, mixOver(hex, LIGHT_WASH_FRACTION, LIGHT_RAISED_2)) < MIN_CONTRAST) return false;
  return true;
}

function darkMeets(hex: string): boolean {
  if (contrastRatio(hex, DARK_RAISED) < MIN_CONTRAST) return false;
  if (contrastRatio(hex, DARK_GROUND) < MIN_CONTRAST) return false;
  if (contrastRatio(hex, DARK_RAISED_2) < MIN_CONTRAST) return false;
  // The dark wash is a true translucent `rgb(r g b / 13%)` (unlike the light
  // one above), so what it reads against depends on which surface it sits on
  // — checked composited over both.
  if (contrastRatio(hex, mixOver(hex, DARK_WASH_ALPHA / 100, DARK_RAISED)) < MIN_CONTRAST) return false;
  if (contrastRatio(hex, mixOver(hex, DARK_WASH_ALPHA / 100, DARK_RAISED_2)) < MIN_CONTRAST) return false;
  return true;
}

/**
 * Walk lightness from the seed towards black, in `STEPS` even steps, and
 * return the first candidate that reads on every light surface. Always
 * terminates: the last step is pure black, which reads against white by
 * construction (21:1), so this never falls through to the fallback below —
 * it exists only so TypeScript sees a return on every path.
 */
function searchLight(h: number, s: number, l0: number): string {
  for (let i = 0; i <= STEPS; i++) {
    const l = l0 - (i / STEPS) * l0;
    const candidate = hslToHex(h, s, l);
    if (lightMeets(candidate)) return candidate;
  }
  return '#000000';
}

/** The dark-theme mirror of {@link searchLight}: walks towards white. */
function searchDark(h: number, s: number, l0: number): string {
  for (let i = 0; i <= STEPS; i++) {
    const l = l0 + (i / STEPS) * (1 - l0);
    const candidate = hslToHex(h, s, l);
    if (darkMeets(candidate)) return candidate;
  }
  return '#ffffff';
}

/**
 * The dark-theme ink: a near-black *of the seed's own hue* (the way the four
 * shipped `--ad-ink` values are — e.g. operator's #0a1633 is a dark navy, not
 * a flat grey) when that holds against `ad`; a plain near-black next; white
 * last. The middle step is defensive rather than reachable in practice —
 * `ad` is already forced light enough to read on near-black surfaces, so a
 * near-black ink read against it by a wide margin.
 */
function deriveAdInk(ad: string): string {
  const { h, s } = hexToHsl(ad);
  const hued = hslToHex(h, Math.min(s, 0.65), 0.1);
  if (contrastRatio(hued, ad) >= MIN_CONTRAST) return hued;
  if (contrastRatio(DARK_NEAR_BLACK, ad) >= MIN_CONTRAST) return DARK_NEAR_BLACK;
  return '#ffffff';
}

/**
 * The eight `--a*`/`--ad*` tokens a hex expands into (§12.4), moving
 * lightness only until every contrast promise in §7 holds, for both themes.
 *
 * The seed itself is kept verbatim (not round-tripped through HSL, which
 * would risk a one-unit rounding drift) whenever it already passes — that is
 * the only way `adjusted` can honestly mean "this exact hex was not good
 * enough on its own".
 */
export function accentTokens(hexInput: string): AccentTokens {
  const hex = hexInput.toLowerCase();
  const { h, s, l } = hexToHsl(hex);

  const adjusted = !lightMeets(hex);
  const a = adjusted ? searchLight(h, s, l) : hex;
  const aWash = mixOverWhite(a, LIGHT_WASH_FRACTION);
  // Contrast is symmetric (§7's "text on X" and "X's own ink" are the same
  // pair), so this reuses the exact check `a` was searched against on
  // `LIGHT_RAISED` (#ffffff) rather than re-deriving a second threshold.
  const aInk = contrastRatio('#ffffff', a) >= MIN_CONTRAST ? '#ffffff' : LIGHT_INK;
  const aDeep = withLightness(a, -DEEP_DELTA);

  const adjustedDark = !darkMeets(hex);
  const ad = adjustedDark ? searchDark(h, s, l) : hex;
  const adWash = rgbSpacePercent(ad, DARK_WASH_ALPHA);
  const adInk = deriveAdInk(ad);
  const adDeep = withLightness(ad, DEEP_DELTA);

  return {
    light: { a, aDeep, aInk, aWash },
    dark: { ad, adDeep, adInk, adWash },
    adjusted,
    adjustedDark,
  };
}

/* ------------------------------------------------------------------ *
 * Clashing with a state colour (§4's tone table: an accent must never be
 * mistaken for provisional/overdue/locked)
 * ------------------------------------------------------------------ */

/** Both themes' hue for each state colour — a brand's accent is picked once and lives in both. */
const OVERDUE_HUES = [hexToHsl('#b3261e').h, hexToHsl('#f47a70').h] as const;
const LOCKED_HUES = [hexToHsl('#1b7542').h, hexToHsl('#5fcf8e').h] as const;
const CLASH_DEGREES = 20;

function hueDistance(a: number, b: number): number {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Is this hex close enough to overdue red or locked green to read as one of
 * them at a glance? A caution, not a refusal (§12.4 screens show it as a
 * note, not a validation error) — the state still shows in its shape (§2),
 * so a clashing accent is a readability nuisance, never a correctness bug.
 *
 * Near-grey hexes (low saturation) have no stable hue to compare, so they
 * never clash — a grey accent cannot be mistaken for a red or green mark.
 */
export function accentStateClash(hex: string): 'overdue' | 'locked' | null {
  const { h, s } = hexToHsl(hex);
  if (s < 0.08) return null;
  if (OVERDUE_HUES.some((oh) => hueDistance(h, oh) <= CLASH_DEGREES)) return 'overdue';
  if (LOCKED_HUES.some((lh) => hueDistance(h, lh) <= CLASH_DEGREES)) return 'locked';
  return null;
}

/**
 * Eight hues, tuned so each already holds §7's contrast in light without
 * `accentTokens` having to move it (`adjusted` is false for every one —
 * `test/accent.test.ts` holds that). Spread around the wheel rather than
 * picked for "brand-safe" respectability, because a real brand's colour will
 * be neither.
 */
export const ACCENT_PRESETS: readonly { readonly hex: string; readonly nameKey: KitMessageKey }[] = [
  // Not orchid: the brand console's own stock accent *is* #ad2b7c (styles.css
  // `:root[data-portal='brand']`), so offering it again here as a preset gave
  // a brand owner two ways to land on "the same colour" that then rendered
  // differently in dark theme (this file derives `dark.ad` algorithmically;
  // the shipped brand console uses a hand-picked #f07fc5) — picking "Orchid"
  // looked like a different choice from "Standard colour" and wasn't. Slate
  // stands apart from every other preset and every console's own accent by
  // being the one desaturated hue in the set (19% vs. 58%+ everywhere else),
  // so it reads as its own colour rather than a muted version of one already
  // on offer.
  { hex: '#475569', nameKey: 'accent.preset.slate' },
  { hex: '#9f1245', nameKey: 'accent.preset.ruby' },
  { hex: '#a3500a', nameKey: 'accent.preset.ember' },
  // A hair darker than #9c5f07: that hex read on `--raised` and `--ground`
  // but fell to 4.33:1 against its own wash composited over `--raised-2`
  // (§12.1 finding — `darkMeets`/`lightMeets` used to check solid surfaces
  // only) — #975c07 is the same hue and saturation, moved just far enough to
  // clear all four.
  { hex: '#975c07', nameKey: 'accent.preset.gold' },
  { hex: '#3f6212', nameKey: 'accent.preset.olive' },
  { hex: '#0f766e', nameKey: 'accent.preset.teal' },
  { hex: '#0369a1', nameKey: 'accent.preset.ocean' },
  { hex: '#4338ca', nameKey: 'accent.preset.indigo' },
] as const;

/* ------------------------------------------------------------------ *
 * The one place this file touches the DOM
 * ------------------------------------------------------------------ */

const OVERRIDE_PROPS = [
  '--a', '--a-deep', '--a-ink', '--a-wash',
  '--ad', '--ad-deep', '--ad-ink', '--ad-wash',
] as const;

/**
 * A brand's colour, applied over its console's own (§12.4). The theme
 * blocks in styles.css already read `--accent: var(--a)` etc. on `:root`, so
 * setting these eight inline on `<html>` is enough to win — nothing in the
 * stylesheet has to know a brand is involved. Mirrors `usePortal` in
 * shell.tsx: a layout effect, so the override lands before paint.
 */
export function useAccentOverride(hex: string | null | undefined): void {
  useLayoutEffect(() => {
    if (!hex || !isAccentHex(hex)) return;
    const { light, dark } = accentTokens(hex);
    const root = document.documentElement.style;
    root.setProperty('--a', light.a);
    root.setProperty('--a-deep', light.aDeep);
    root.setProperty('--a-ink', light.aInk);
    root.setProperty('--a-wash', light.aWash);
    root.setProperty('--ad', dark.ad);
    root.setProperty('--ad-deep', dark.adDeep);
    root.setProperty('--ad-ink', dark.adInk);
    root.setProperty('--ad-wash', dark.adWash);
    return () => {
      for (const prop of OVERRIDE_PROPS) root.removeProperty(prop);
    };
  }, [hex]);
}
